import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2, Lock, LockOpen } from "lucide-react";
import { DATA_CHANGED_EVENT, request } from "../lib/api";
import type { Notice, StructureItem, StudySetupResponse } from "../types";
import { formatDate } from "../lib/datetime";
import {
  Badge,
  Card,
  ConfirmModal,
  InlineNotice,
  Table,
  dangerButtonClass,
  errorMessage,
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  textareaClass,
} from "../components/ui";

// A cohort's arms in one place: its groups and how many teachers each has,
// what looks wrong, and the lock that freezes who is in which group once the
// study is under way.
export function StudySetup({
  cohorts,
  onChanged,
}: {
  cohorts: StructureItem[];
  onChanged: () => void;
}) {
  const [picked, setPicked] = useState("");
  const cohortID = picked || cohorts[0]?.id || "";
  const [setup, setSetup] = useState<StudySetupResponse | null>(null);
  const [message, setMessage] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingLock, setConfirmingLock] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [reason, setReason] = useState("");
  const [reload, setReload] = useState(0);

  // Any change made on this page, or by importing teachers, shows up here.
  useEffect(() => {
    const bump = () => setReload((count) => count + 1);
    window.addEventListener(DATA_CHANGED_EVENT, bump);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, bump);
  }, []);

  const load = useCallback(() => {
    if (!cohortID) return;
    request<StudySetupResponse>(`/api/admin/cohorts/${cohortID}/study-setup`)
      .then((data) => setSetup(data))
      .catch((cause) => setMessage({ tone: "error", text: errorMessage(cause) }));
  }, [cohortID]);
  useEffect(() => {
    load();
  }, [load, reload]);

  async function act(path: string, body?: object) {
    setBusy(true);
    setMessage(null);
    try {
      await request(`/api/admin/cohorts/${cohortID}/${path}`, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      });
      setConfirmingLock(false);
      setUnlocking(false);
      setReason("");
      load();
      onChanged();
    } catch (cause) {
      setConfirmingLock(false);
      setMessage({ tone: "error", text: errorMessage(cause) });
    } finally {
      setBusy(false);
    }
  }

  if (!cohorts.length) return null;
  const locked = Boolean(setup?.locked_at);
  return (
    <Card
      title="Study set-up"
      text="Check a cohort's groups before the study starts, then lock who is in which group."
    >
      <label className={`${labelClass} mb-4 max-w-sm`}>
        Cohort
        <select
          className={inputClass}
          value={cohortID}
          onChange={(event) => {
            setSetup(null);
            setMessage(null);
            setPicked(event.target.value);
          }}
        >
          {cohorts.map((cohort) => (
            <option key={cohort.id} value={cohort.id}>
              {cohort.name}
            </option>
          ))}
        </select>
      </label>
      {message && <InlineNotice tone={message.tone} text={message.text} />}
      {setup && (
        <div className="grid gap-4">
          <Table
            headers={[{ label: "Group" }, { label: "Arm" }, { label: "Teachers" }]}
            rows={setup.groups.map((group) => [
              <span key="name">
                <strong className="font-semibold">{group.name}</strong>
                <span className="block text-xs text-content-secondary">{group.code}</span>
              </span>,
              group.arm_kind ? (
                <Badge key="arm" tone={group.arm_kind === "control" ? "neutral" : "success"}>
                  {group.arm_kind}
                </Badge>
              ) : (
                <span key="arm" className="text-xs text-danger">
                  Not set
                </span>
              ),
              group.teachers.toLocaleString(),
            ])}
          />
          <p className="text-sm text-content-secondary">
            {setup.teachers.toLocaleString()} teachers in this cohort's schools;{" "}
            {setup.teachers_without_group.toLocaleString()} in no group yet.
          </p>
          {setup.warnings.length > 0 && (
            <ul
              className="grid gap-1.5 rounded-md bg-warning-bg px-4 py-3 text-sm text-warning-text"
              aria-label="Things to check"
            >
              {setup.warnings.map((warning) => (
                <li key={warning} className="flex items-start gap-2">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  {warning}
                </li>
              ))}
            </ul>
          )}
          {locked ? (
            <div className="grid gap-3 rounded-md border border-border p-4">
              <p className="flex items-center gap-2 text-sm font-medium text-content-primary">
                <Lock size={16} />
                Locked {formatDate(setup.locked_at)}
                {setup.locked_by ? ` by ${setup.locked_by}` : ""}. A teacher's group cannot change.
                Teachers who join later can still be given a group once.
              </p>
              {unlocking ? (
                <div className="grid gap-3">
                  <label className={labelClass}>
                    Why unlock? This is kept in the audit log.
                    <textarea
                      className={textareaClass}
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      className={dangerButtonClass}
                      disabled={busy || reason.trim().length < 10}
                      onClick={() => void act("unlock-assignments", { reason })}
                    >
                      {busy && <Loader2 size={16} className="animate-spin" />}
                      Unlock assignments
                    </button>
                    <button
                      type="button"
                      className={secondaryButtonClass}
                      onClick={() => {
                        setUnlocking(false);
                        setReason("");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className={`${secondaryButtonClass} w-fit`}
                  onClick={() => setUnlocking(true)}
                >
                  <LockOpen size={16} />
                  Unlock…
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              className={`${primaryButtonClass} w-fit`}
              onClick={() => setConfirmingLock(true)}
              disabled={busy}
            >
              <Lock size={16} />
              Lock assignments
            </button>
          )}
        </div>
      )}
      <ConfirmModal
        open={confirmingLock}
        title="Lock this cohort's assignments?"
        message="Once locked, a teacher's group cannot be changed or removed, and a group's treatment/control mark cannot change. Teachers who join later can still be given a group once. Unlocking needs a written reason."
        confirmLabel="Lock"
        busy={busy}
        onConfirm={() => void act("lock-assignments")}
        onCancel={() => setConfirmingLock(false)}
      />
    </Card>
  );
}
